"use client";

import { usePathname, useSearchParams } from "next/navigation";
import Script from "next/script";
import { useEffect, useRef } from "react";

const PIXEL_ID_REGEX = /^\d{15,16}$/;

const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
  "gclid",
  "msclkid",
] as const;

function isValidPixelId(id: string | undefined): id is string {
  return typeof id === "string" && id.length > 0 && PIXEL_ID_REGEX.test(id);
}

export function FacebookPixelProvider({ enabled }: { enabled: boolean }) {
  // Read at render-time so test order / module caching can't lock in an undefined value.
  const pixelId = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isInitialMount = useRef(true);

  // We do NOT want a new Facebook "PageView" every time the user tweaks in-page UI state
  // that we store in the URL (examples: `?filter=...`, `?sort=...`, `?page=...`).
  //
  // We ONLY treat query-string changes as a new "PageView" when they affect ad attribution
  // (examples: `utm_*`, `fbclid`, `gclid`, `msclkid`).
  const attributionKey = ATTRIBUTION_KEYS.map((key) => `${key}=${searchParams.get(key) ?? ""}`)
    .filter((pair) => !pair.endsWith("="))
    .join("&");

  // When `pathname` changes: new page -> fire PageView.
  // When only `attributionKey` changes: attribution changed -> fire PageView.
  // When only unrelated query params change (filters/sort/pagination): do NOT fire PageView.
  // Initial PageView is fired by the script below, so we skip the first effect run.
  //
  // Flip isInitialMount BEFORE the fbq check. If the script loads late, the first effect run
  // returns early (no fbq yet) but we still consume "initial mount." The next run (e.g. route
  // change) will not be incorrectly skipped.
  useEffect(() => {
    if (!isValidPixelId(pixelId) || !enabled) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip: script already fired (or will fire) initial PageView
    }

    if (typeof window === "undefined" || typeof window.fbq !== "function") return;

    window.fbq("track", "PageView");
  }, [pathname, attributionKey, enabled, pixelId]);

  if (!isValidPixelId(pixelId) || !enabled) {
    return null;
  }

  return (
    <>
      <Script id="facebook-pixel" strategy="afterInteractive">
        {`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixelId}');
          fbq('track', 'PageView');
        `}
      </Script>
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          alt=""
          src={`https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1`}
        />
      </noscript>
    </>
  );
}
